import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AlertTriangle, Clock, X, ExternalLink } from "lucide-react";

/**
 * Hook — busca a cobrança em aberto do usuário logado.
 * Faz polling a cada 60s quando o usuário está autenticado.
 */
export function useCurrentCharge() {
  const { user } = useAuth();
  const [charge, setCharge] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!user) { setCharge(null); return; }
    let stopped = false;
    const load = async () => {
      try {
        const { data } = await api.get("/payments/asaas/current-charge");
        if (!stopped) setCharge(data);
      } catch { /* ignore */ }
    };
    load();
    timer.current = setInterval(load, 60000);
    return () => { stopped = true; clearInterval(timer.current); };
  }, [user]);

  return charge;
}

/**
 * Banner no topo do dashboard — aparece 3 dias antes do vencimento ou se vencido.
 * Dismissível por sessão (usuário pode fechar e volta a aparecer no próximo load).
 */
export function RenewalBanner() {
  const charge = useCurrentCharge();
  const [dismissed, setDismissed] = useState(false);

  if (!charge?.show_banner || dismissed) return null;

  const isOverdue = charge.is_overdue;
  const days = charge.days_until_due;
  const value = Number(charge.value || 0).toFixed(2).replace(".", ",");

  // Mensagem adaptativa
  let title, subtitle;
  if (isOverdue) {
    title = "Sua assinatura está vencida";
    subtitle = `Pague agora para manter o acesso Premium. Após 3 dias úteis em atraso, a conta volta para o plano gratuito automaticamente.`;
  } else if (days === 0) {
    title = "Sua assinatura vence hoje";
    subtitle = `Pague por PIX ou Cartão sem sair do site para renovar o Premium.`;
  } else if (days === 1) {
    title = "Sua assinatura vence amanhã";
    subtitle = `Pague por PIX ou Cartão para evitar interrupções no acesso.`;
  } else {
    title = `Sua assinatura vence em ${days} dias`;
    subtitle = `Pague por PIX ou Cartão sem sair do site para renovar o Premium.`;
  }

  const palette = isOverdue
    ? { bg: "bg-red-50", border: "border-red-300", text: "text-red-800", btn: "bg-red-600 hover:bg-red-700", icon: AlertTriangle }
    : { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", btn: "bg-amber-600 hover:bg-amber-700", icon: Clock };
  const Icon = palette.icon;

  return (
    <div
      className={`${palette.bg} ${palette.border} border-b-2 px-4 py-3 sm:px-6`}
      data-testid="renewal-banner"
    >
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className={`flex-shrink-0 ${palette.text}`}>
          <Icon size={22} strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-heading font-bold text-sm sm:text-base ${palette.text}`} data-testid="renewal-banner-title">
            {title} — R$ {value}
          </div>
          <div className={`text-xs sm:text-sm ${palette.text} opacity-90`}>{subtitle}</div>
        </div>
        {charge.invoice_url && (
          <a
            href={charge.invoice_url}
            target="_blank"
            rel="noopener noreferrer"
            className={`${palette.btn} inline-flex items-center gap-1.5 text-white text-sm font-bold px-4 py-2 rounded-full whitespace-nowrap transition-colors`}
            data-testid="renewal-pay-btn"
          >
            Pagar agora <ExternalLink size={14} />
          </a>
        )}
        <button
          onClick={() => setDismissed(true)}
          className={`${palette.text} opacity-60 hover:opacity-100`}
          aria-label="Fechar"
          data-testid="renewal-dismiss"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
